// v3.0.0-rc.7-phaseCD-CD_WALLET_RICH_PURCHASE_V1 — Phase C.2 · WalletB2cPage (B2C utilisateur final)
//
// Portefeuille B2C user-facing (palette vert émeraude + accent gold), distinct du Wallet PRO bleu marine.
// Endpoints consommés (préfixe /api/v3/wallet — confirmé via grep server.js+walletB2cRoute.js):
//   GET /api/v3/wallet/balance        → { ok, account, balance_fcfa }
//   GET /api/v3/wallet/transactions   → { ok, total, items[] }
//
// Auth : token JWT stocké localStorage['civ_user_token'] + profil { phone } sous localStorage['civ_user_profile'].
// Architecture : composant feuille, zéro touche aux stores ni au backend (utilise apiFetch).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { apiFetch } from "@/utils/apiClient";
import WalletTopupModal from "@/components/WalletTopupModal";
// [H_UIPOLISH_V1_L4] Polish-only : visual asset wallet card 3D.
import AyaAsset from "@/components/ui/AyaAsset";
// [H_UIPOLISH_V1_L3 Palier 2.A] Polish-only imports (CountUp / Skeleton / Toast).
// Aucun changement de logique : ces composants sont purement visuels.
import { AyaCountUp } from "@/components/ui/CountUp";
import { AyaSkeleton } from "@/components/ui/Skeleton";
import { AyaToastProvider } from "@/components/ui/Toast";

const PROFILE_KEY = "civ_user_profile";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Palette vert émeraude + gold (distincte du Wallet PRO bleu marine).
const C = {
  emeraldDeep: "#0F2E20",
  emeraldMid: "#1B5436",
  emeraldLight: "#2A7350",
  gold: "#D4AF37",
  goldSoft: "rgba(212, 175, 55, 0.18)",
  ivory: "#F5F1E6",
  ivoryDim: "rgba(245, 241, 230, 0.72)",
  ivoryFade: "rgba(245, 241, 230, 0.42)",
  surfaceLine: "rgba(212, 175, 55, 0.22)",
  errorBg: "rgba(244, 67, 54, 0.14)",
  errorFg: "#ffb4ab",
  creditFg: "#9bd9b1",
  debitFg: "#ffb1a8",
};

function _readProfile() {
  try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || "null"); } catch { return null; }
}

function _fmtCFA(n) {
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("fr-FR").format(Math.round(n)) + " FCFA";
}

function _fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return String(iso); }
}

function _txLabel(it) {
  if (it.transaction_type === "wallet_topup_mock") {
    const prov = it.provider ? ` · ${String(it.provider).toUpperCase()}` : "";
    return `Recharge${prov}`;
  }
  if (it.transaction_type === "wallet_purchase") return `Achat${it.counterparty ? ` · ${it.counterparty}` : ""}`;
  if (it.type === "credit") return "Crédit";
  if (it.type === "debit") return "Débit";
  return it.transaction_type || it.type || "Mouvement";
}

export default function WalletB2cPage() {
  const navigate = useNavigate();
  const profile = useMemo(_readProfile, []);
  const [balance, setBalance] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topupOpen, setTopupOpen] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [rBal, rTx] = await Promise.all([
        apiFetch(`${BACKEND}/api/v3/wallet/balance`),
        apiFetch(`${BACKEND}/api/v3/wallet/transactions?limit=30&offset=0`),
      ]);
      if (!rBal.ok) throw new Error(`balance_http_${rBal.status}`);
      if (!rTx.ok) throw new Error(`transactions_http_${rTx.status}`);
      const bal = await rBal.json();
      const tx = await rTx.json();
      if (bal?.ok) setBalance(Number(bal.balance_fcfa) || 0);
      if (tx?.ok) { setItems(Array.isArray(tx.items) ? tx.items : []); setTotal(Number(tx.total) || 0); }
    } catch (e) {
      setError(e?.message || "Erreur réseau. Vérifie ta connexion.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleTopupSuccess = useCallback((newBalance) => {
    setTopupOpen(false);
    if (Number.isFinite(newBalance)) setBalance(newBalance);
    // Rafraîchit la liste transactions (la balance vient déjà du payload topup).
    refresh();
  }, [refresh]);

  return (
    <div
      data-testid="wallet-b2c-page"
      style={{
        minHeight: "100vh",
        background: `linear-gradient(160deg, ${C.emeraldDeep} 0%, ${C.emeraldMid} 55%, ${C.emeraldLight} 100%)`,
        color: C.ivory,
        padding: "16px 14px 60px",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <header style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
        <button
          data-testid="wallet-b2c-back-btn"
          onClick={() => navigate("/")}
          style={{ background: "transparent", color: C.ivory, border: `1px solid ${C.surfaceLine}`, borderRadius: 999, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}
        >
          ← Retour
        </button>
        <div style={{ marginLeft: "auto", fontSize: 12, color: C.ivoryFade, letterSpacing: 0.4 }}>
          {profile?.phone || "—"}
        </div>
      </header>

      {/* Hero solde */}
      <section
        data-testid="wallet-b2c-hero"
        className="aya-motion-fade-in-up aya-wallet-card-3d"
        style={{
          borderRadius: 22,
          padding: "22px 20px 24px",
          background: `linear-gradient(135deg, rgba(15,46,32,0.92), rgba(27,84,54,0.78))`,
          border: `1px solid ${C.surfaceLine}`,
          boxShadow: "0 10px 30px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05), var(--aya-shadow-glow-gold)",
          position: "relative",
          overflow: "hidden",
          backdropFilter: "blur(var(--aya-glass-blur-md)) saturate(var(--aya-glass-saturate))",
          WebkitBackdropFilter: "blur(var(--aya-glass-blur-md)) saturate(var(--aya-glass-saturate))",
        }}
      >
        {/* [L4] Illustration 3D décorative — pos absolue, n'impacte pas la lisibilité texte */}
        <AyaAsset
          name="wallet.card_3d"
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            right: -28,
            top: -10,
            width: 200,
            pointerEvents: "none",
            filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.35))",
          }}
        />
        <div style={{ position: "absolute", top: -30, right: -30, width: 160, height: 160, background: C.goldSoft, borderRadius: "50%", filter: "blur(40px)" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.gold, boxShadow: `0 0 8px ${C.gold}` }} />
          <span style={{ fontSize: 11, letterSpacing: 1.4, color: C.gold, fontWeight: 600, textTransform: "uppercase" }}>Portefeuille AYA</span>
        </div>
        <div data-testid="wallet-b2c-balance-label" style={{ fontSize: 12, color: C.ivoryDim, marginBottom: 4 }}>Solde disponible</div>
        <div
          data-testid="wallet-b2c-balance-amount"
          style={{ fontSize: 36, fontWeight: 700, letterSpacing: -0.5, color: C.ivory, fontFeatureSettings: "'tnum'", minHeight: 44 }}
        >
          {loading ? (
            <AyaSkeleton
              data-testid="wallet-b2c-balance-skeleton"
              width={210}
              height={36}
              radius="var(--aya-radius-md)"
              style={{ backgroundColor: "rgba(212, 175, 55, 0.12)" }}
            />
          ) : (
            <AyaCountUp
              value={Number(balance ?? 0)}
              duration={900}
              suffix=" FCFA"
              locale="fr-FR"
              aria-label={_fmtCFA(balance ?? 0)}
            />
          )}
        </div>
        <button
          data-testid="wallet-b2c-topup-btn"
          onClick={() => setTopupOpen(true)}
          disabled={loading}
          style={{
            marginTop: 18,
            width: "100%",
            background: C.gold,
            color: C.emeraldDeep,
            border: "none",
            borderRadius: 14,
            padding: "13px 18px",
            fontSize: 15,
            fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: "0 6px 16px rgba(212, 175, 55, 0.34)",
            letterSpacing: 0.3,
          }}
        >
          + Recharger mon portefeuille
        </button>
      </section>

      {/* Liste transactions */}
      <section style={{ marginTop: 22 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10, padding: "0 4px" }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, letterSpacing: 0.4, color: C.ivory, margin: 0, textTransform: "uppercase" }}>
            Mouvements
          </h2>
          <span data-testid="wallet-b2c-tx-count" style={{ fontSize: 12, color: C.ivoryFade }}>
            {loading ? "…" : `${total} ${total > 1 ? "opérations" : "opération"}`}
          </span>
        </div>

        {error && (
          <div data-testid="wallet-b2c-error" style={{ background: C.errorBg, color: C.errorFg, padding: "10px 14px", borderRadius: 12, fontSize: 13, marginBottom: 10 }}>
            {error}
          </div>
        )}

        {loading && (
          <div
            data-testid="wallet-b2c-loading"
            style={{ padding: "4px 0", display: "flex", flexDirection: "column", gap: 8 }}
          >
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 14px",
                  background: "rgba(15,46,32,0.4)",
                  border: `1px solid ${C.surfaceLine}`,
                  borderRadius: 14,
                }}
              >
                <AyaSkeleton variant="circle" width={38} height={38} style={{ backgroundColor: "rgba(212, 175, 55, 0.10)" }} />
                <div style={{ flex: 1 }}>
                  <AyaSkeleton width="55%" height={12} style={{ backgroundColor: "rgba(212, 175, 55, 0.10)" }} />
                  <AyaSkeleton width="32%" height={10} style={{ marginTop: 6, backgroundColor: "rgba(212, 175, 55, 0.08)" }} />
                </div>
                <AyaSkeleton width={72} height={14} style={{ backgroundColor: "rgba(212, 175, 55, 0.10)" }} />
              </div>
            ))}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div
            data-testid="wallet-b2c-empty"
            style={{
              padding: "28px 18px",
              textAlign: "center",
              borderRadius: 16,
              background: "rgba(15,46,32,0.6)",
              border: `1px dashed ${C.surfaceLine}`,
              color: C.ivoryDim,
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            <div style={{ fontSize: 26, marginBottom: 8 }}>💎</div>
            Aucun mouvement pour l&apos;instant.<br />
            Recharge ton portefeuille pour commencer.
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <ul data-testid="wallet-b2c-tx-list" style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map((it, idx) => {
              const isCredit = it.type === "credit";
              return (
                <m.li
                  key={it.ledger_entry_id || idx}
                  data-testid={`wallet-b2c-tx-item-${idx}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.32,
                    delay: Math.min(idx, 8) * 0.04,
                    ease: [0.32, 0.72, 0, 1],
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    background: "rgba(15,46,32,0.55)",
                    border: `1px solid ${C.surfaceLine}`,
                    borderRadius: 14,
                    backdropFilter: "blur(var(--aya-glass-blur-sm))",
                    WebkitBackdropFilter: "blur(var(--aya-glass-blur-sm))",
                  }}
                >
                  <div style={{
                    width: 38, height: 38, borderRadius: 12,
                    background: isCredit ? "rgba(155, 217, 177, 0.18)" : "rgba(255, 177, 168, 0.18)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: isCredit ? C.creditFg : C.debitFg, fontSize: 18, fontWeight: 700,
                  }}>
                    {isCredit ? "+" : "−"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.ivory, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {_txLabel(it)}
                    </div>
                    <div style={{ fontSize: 11, color: C.ivoryFade, marginTop: 2 }}>{_fmtDate(it.created_at)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: isCredit ? C.creditFg : C.debitFg, fontFeatureSettings: "'tnum'" }}>
                      {isCredit ? "+" : "−"}{_fmtCFA(Math.abs(Number(it.amount_cfa) || 0))}
                    </div>
                    {Number.isFinite(Number(it.balance_after_cfa)) && (
                      <div style={{ fontSize: 10, color: C.ivoryFade, marginTop: 2, fontFeatureSettings: "'tnum'" }}>
                        Solde : {_fmtCFA(it.balance_after_cfa)}
                      </div>
                    )}
                  </div>
                </m.li>
              );
            })}
          </ul>
        )}
      </section>

      <WalletTopupModal
        open={topupOpen}
        onClose={() => setTopupOpen(false)}
        defaultPhone={profile?.phone || ""}
        onSuccess={handleTopupSuccess}
      />
    </div>
  );
}
